import { computed, action, makeObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { TrpcClient } from 'src/appState/TrpcClient';
import { getSavedCards, getSavedCardsWithNetDeposits } from 'src_server/trpc/types/payments';
import { DateTime } from 'src_common/utils/time/time';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { WebsocketV1 } from 'src/domains/layouts/state/websocketV1/WebsocketV1';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { ApiResponseWrapper } from 'src_common/common/mobx-utils/ApiResponseWrapper';
import { Common } from 'src/domains/common/Common';
import { MultiDepositPaymentProviderType } from 'src/domains/layouts/config/features/types';

const isCardExpired = (card: getSavedCards.SavedCardInterfaceType): boolean => {
    return DateTime.current().isBefore(DateTime.from(card.expires ?? undefined) ?? DateTime.current());
};

//It works bad in backend. LastUpdatedAt return createdAtDate.
const sortByLastUpdatedAt = (
    a: getSavedCards.SavedCardInterfaceType,
    b: getSavedCards.SavedCardInterfaceType
): number => {
    const lastUpadedA = a.lastUsedAt ?? null;
    const lastUpadedB = b.lastUsedAt ?? null;

    if (lastUpadedA === null && lastUpadedB === null) {
        return 0;
    }

    if (lastUpadedA === null) {
        return -1;
    }

    if (lastUpadedB === null) {
        return 1;
    }

    return DateTime.from(lastUpadedA)?.isBefore(lastUpadedB) === true ? 1 : -1;
};

export class PaymentMethodDropdownState {
    public readonly selectedTopUpOption: FormInputState<string, string>;
    public readonly savedPaymentMethodResource: Resource<getSavedCards.ResponseType>;
    public readonly savedOpenBankingResource: Resource<getSavedCards.ResponseType | null>;
    public readonly savedCardsWithNetDepositsResponseWrapper: AutoMap<
        [number],
        ApiResponseWrapper<getSavedCardsWithNetDeposits.ResponseType | null>
    >;
    private readonly paymentProviders: MultiDepositPaymentProviderType;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly basicDataModel: BasicDataModel,
        private readonly common: Common,
        private readonly savedMethodsProvidersOverride: string[] | null = null
    ) {
        makeObservable(this);

        this.paymentProviders = this.common.features.paymentProviders;
        this.selectedTopUpOption = FormInputState.new('');
        this.savedPaymentMethodResource = new Resource(async (): Promise<getSavedCards.ResponseType> => {
            const savedMethodsProviders =
                this.savedMethodsProvidersOverride ??
                (this.paymentProviders.isNewPaymentJourneyEnabled === true
                    ? ['realex', 'paysafe']
                    : this.paymentProviders.debitCard.type === null
                      ? null
                      : [this.paymentProviders.debitCard.type]);

            if (savedMethodsProviders === null) {
                return {
                    response: [],
                    responseStatus: 'success',
                };
            }

            const result = await this.trpcClient.client.payments.getSavedCard.query({
                providers: savedMethodsProviders,
            });
            return result.responseStatus === 'success'
                ? { ...result, response: this.filterSavedWithdrawalMethods(result.response) }
                : result;
        });

        this.savedOpenBankingResource = new Resource(async (): Promise<getSavedCards.ResponseType | null> => {
            const openBanking =
                this.paymentProviders.openBanking ??
                (this.paymentProviders.isNewPaymentJourneyEnabled === true ? this.paymentProviders.openBanking : null);

            if (openBanking?.type == null) {
                return null;
            }

            return await this.trpcClient.client.payments.getSavedCard.query({
                providers: [openBanking.type],
            });
        });

        this.savedCardsWithNetDepositsResponseWrapper = new AutoMap(
            ([userId]) =>
                new ApiResponseWrapper(
                    'getSavedCardsWithNetDeposits',
                    null,
                    async (): Promise<getSavedCardsWithNetDeposits.ResponseType> => {
                        const savedMethodsProviders =
                            this.savedMethodsProvidersOverride ??
                            (this.common.features.paymentProviders.isNewPaymentJourneyEnabled === true
                                ? ['realex', 'paysafe']
                                : this.paymentProviders.debitCard.type === null
                                  ? null
                                  : [this.paymentProviders.debitCard.type]);

                        if (savedMethodsProviders === null) {
                            return {
                                response: [],
                                responseStatus: 'success' as const,
                            };
                        }

                        const request = await this.trpcClient.client.payments.getSavedCardsWithNetDeposits.query({
                            providers: savedMethodsProviders,
                        });
                        return request.responseStatus === 'success'
                            ? { ...request, response: this.filterSavedWithdrawalMethods(request.response) }
                            : request;
                    },
                    (refresh) => {
                        refresh();

                        WebsocketV1.get(this.common).subscribe(`${userId}:Wallet`, refresh);

                        return (): void => {
                            WebsocketV1.get(this.common).unsubscribe(`${userId}:Wallet`, refresh);
                        };
                    }
                )
        );
    }

    // With Worldpay cards and Paysafe Apple Pay the Worldpay withdrawal requests both providers.
    // Keep each method only when its provider is the one configured for its type.
    private filterSavedWithdrawalMethods<T extends getSavedCards.SavedCardInterfaceType>(methods: T[]): T[] {
        if (
            this.savedMethodsProvidersOverride?.includes('paysafe') !== true ||
            this.savedMethodsProvidersOverride.includes('worldpay') !== true
        ) {
            return methods;
        }

        return methods.filter((method) => {
            if (method.subtype === 'apple-pay') return method.provider === this.paymentProviders.applePay?.type;
            if (method.subtype === 'google-pay') return method.provider === this.paymentProviders.googlePay?.type;
            return method.provider === this.paymentProviders.debitCard.type;
        });
    }

    @action public refreshPaymentMethods = async (): Promise<void> => {
        if (this.common.features.withdrawDropdownNew === true) {
            const userId = this.common.session.userId;
            if (userId === null) {
                return;
            }
            this.savedCardsWithNetDepositsResponseWrapper.get([userId]).refresh();
        } else {
            await this.savedPaymentMethodResource.refresh();
        }
        if (this.paymentProviders.openBanking !== null) {
            await this.savedOpenBankingResource.refresh();
        }
    };

    @computed public get savedOpenBankingMethods(): getSavedCards.SuccessResponseType {
        if (this.basicDataModel.currency === 'EUR' && this.paymentProviders.openBanking?.isEuroAllowed === false) {
            return [];
        }

        const resource = this.savedOpenBankingResource.getReady();
        if (resource === null || resource.responseStatus === 'error') {
            return [];
        }

        return resource.response;
    }

    @computed public get savedPaymentMethods(): getSavedCards.SuccessResponseType | null {
        const resource = this.savedPaymentMethodResource.getReady();
        if (resource === null || resource.responseStatus === 'error') {
            return null;
        }

        return resource.response;
    }

    @computed public get savedPaymentMethodsWithNetDeposits(): getSavedCardsWithNetDeposits.SuccessResponseType | null {
        const userId = this.common.session.userId;
        if (userId === null) {
            return [];
        }
        const request = this.savedCardsWithNetDepositsResponseWrapper.get([userId]).data;
        if (request !== null && request.responseStatus === 'success') {
            return request.response;
        }

        return null;
    }

    @computed public get paymentMethodsValue(): string {
        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (selectedPaymentMethod === undefined) {
            return 'none';
        }

        return selectedPaymentMethod.id;
    }

    @computed public get selectedPaymentMethod(): getSavedCards.SavedCardInterfaceType | undefined {
        if (this.common.features.withdrawDropdownNew === true) {
            if (this.savedPaymentMethodsWithNetDeposits === null) {
                return undefined;
            }

            if (this.selectedTopUpOption.value === '') {
                const noExpiredPaymentMethods = [...this.savedPaymentMethodsWithNetDeposits]
                    .filter((paymentMethod) => isCardExpired(paymentMethod))
                    .map(({ netDeposit: _, ...card }) => card)
                    .sort(sortByLastUpdatedAt);
                return noExpiredPaymentMethods[0] ?? undefined;
            }

            const accountSavedPaymentMethods = this.savedPaymentMethodsWithNetDeposits.map(({ ...card }) => card);
            for (const paymentMethod of accountSavedPaymentMethods) {
                if (paymentMethod.id === this.selectedTopUpOption.value) {
                    return paymentMethod;
                }
            }
        } else {
            if (this.savedPaymentMethods === null) {
                return undefined;
            }

            if (this.selectedTopUpOption.value === '') {
                const noExpiredPaymentMethods = [...this.savedPaymentMethods]
                    .filter((paymentMethod) => isCardExpired(paymentMethod))
                    .sort(sortByLastUpdatedAt);
                return noExpiredPaymentMethods[0] ?? undefined;
            }

            const accountSavedPaymentMethods = this.savedPaymentMethods;
            for (const paymentMethod of accountSavedPaymentMethods) {
                if (paymentMethod.id === this.selectedTopUpOption.value) {
                    return paymentMethod;
                }
            }
        }
        return undefined;
    }

    @action public handlePaymentSelectChange = (value: string): void => {
        this.selectedTopUpOption.setValue(value);
        this.selectedTopUpOption.setAsVisited();
    };

    @computed public get isNewCard(): boolean {
        return this.paymentMethodsValue === 'none';
    }

    @computed public get areSavedPaymentMethods(): boolean {
        if (this.common.features.withdrawDropdownNew === true) {
            return this.savedPaymentMethodsWithNetDeposits !== null &&
                this.savedPaymentMethodsWithNetDeposits.length > 0
                ? true
                : false;
        }
        return this.savedPaymentMethods !== null && this.savedPaymentMethods.length > 0 ? true : false;
    }
}
